import React, { useState } from "react";
import GenerateLink from "../../helpers/GenerateLink";
import RulerContext from "../../contexts/RulerContext";

const CategorySection: React.FC = () => {
  const ruler = React.useContext(RulerContext);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const ITEMS_TO_SHOW = ruler.xs ? 5 : 8;

  const handleShowMore = (categoryIndex: number) => {
    if (ruler.xs) {
      setSelectedCategory(categoryIndex);
      setShowModal(true);
    } else {
      setExpandedCategory(expandedCategory === categoryIndex ? null : categoryIndex);
    }
  };

  const Modal = ({ category, onClose }: { category: (typeof categories)[0]; onClose: () => void }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-800">{category.title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ✕
          </button>
        </div>
        <div className="p-4">
          {category.subcategories.map((subcategory, subIndex) => (
            <a
              key={subIndex}
              href={GenerateLink.ForSearch(subcategory)}
              className="flex items-center justify-between p-3 border-b last:border-b-0
                       text-gray-600 hover:text-yellow-600 hover:bg-yellow-50"
            >
              <span>{subcategory}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  const categories = [
    {
      title: "Engine Components",
      subcategories: [
        "Piston",
        "Piston Ring",
        "Piston Kit",
        "Valve",
        "Valve Guide",
        "Valve Spring",
        "Intake Valve",
        "Exhaust Valve",
        "Cylinder",
        "Cylinder Head",
        "Crankshaft",
        "Camshaft",
        "Manifold",
        "Turbocharger",
        "Carburetor",
        "Fuel Injector",
        "Gasket",
        "Gasket Kit",
        "Head Gasket",
      ],
    },
    {
      title: "Filtration & Cooling",
      subcategories: [
        "Air Filter",
        "Oil Filter",
        "Fuel Filter",
        "Hydraulic Filter",
        "Cabin Air Filter",
        "Radiator",
        "Oil Cooler",
        "Water Pump",
        "Fan",
        "Thermostat",
        "Air Cleaner",
        "Pre-Cleaner",
      ],
    },
    {
      title: "Transmission & Drive",
      subcategories: [
        "Bearing",
        "Ball Bearing",
        "Needle Bearing",
        "Spherical Bearing",
        "Gear",
        "Shaft",
        "Sprocket",
        "Pulley",
        "Clutch",
        "Clutch Disc",
        "Clutch Kit",
        "Pressure Plate",
        "V-Belt",
        "Drive Belt",
        "Serpentine Belt",
      ],
    },
    {
      title: "Hydraulic Systems",
      subcategories: [
        "Hydraulic Pump",
        "Fuel Pump",
        "Oil Pump",
        "Vane Pump",
        "Seal Kit",
        "O-Ring Kit",
        "Repair Kit",
        "Hydraulic Hose",
        "Fuel Line",
        "Oil Line",
        "Hydraulic Rod",
        "Cylinder Kit",
      ],
    },
    {
      title: "Electrical & Controls",
      subcategories: ["Starter", "Alternator", "Motor", "Generator", "Switch", "Sensor", "Solenoid", "Lamp", "Bulb", "Light"],
    },
    {
      title: "Chassis & Body",
      subcategories: [
        "Tie Rod",
        "Steering Arm",
        "Steering Wheel",
        "Brake Disc",
        "Brake Drum",
        "Brake Pad",
        "Cover",
        "Panel",
        "Hood",
        "Fender",
        "Glass",
        "Mirror",
      ],
    },
    {
      title: "Attachments & Accessories",
      subcategories: ["Blade", "Mower Blade", "Sickle", "Bolt", "Nut", "Washer", "Screw", "Seat", "Cushion", "Guard", "Shield"],
    },
    {
      title: "Maintenance & Repair",
      subcategories: ["Overhaul Kit", "Tune Up Kit", "Service Kit", "Tool Box", "Manual", "Filter", "Grease Fitting"],
    },
  ];

  const manufacturers = [
    { title: "Zbox", link: GenerateLink.ForSearch("Zbox") },
    { title: "John Deere", link: GenerateLink.ForSearch("John Deere") },
    { title: "Caterpillar", link: GenerateLink.ForSearch("Caterpillar") },
    { title: "New Holland", link: GenerateLink.ForSearch("New Holland") },
    { title: "Volvo", link: GenerateLink.ForSearch("Volvo") },
    { title: "Case", link: GenerateLink.ForSearch("Case") },
    { title: "Bush Hog", link: GenerateLink.ForSearch("Bushhog") },
    { title: "Massey Ferguson", link: GenerateLink.ForSearch("Massey Ferguson") },
    { title: "Bobcat", link: GenerateLink.ForSearch("Bobcat") },
    { title: "Ford", link: GenerateLink.ForSearch("Ford") },
  ];

  return (
    <>
      <div className="w-full bg-gradient-to-b from-blue-50 to-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">Shop by Category</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl 
                          transform hover:-translate-y-1 transition-all duration-300"
              >
                <div className="p-6">
                  <h4 className="text-xl font-semibold text-gray-800 mb-4">
                    <span className="relative inline-block pb-2">
                      {category.title}
                      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-yellow-400 to-yellow-600 transform origin-left transition-transform duration-300"></div>
                    </span>
                  </h4>
                  <div className="grid grid-cols-1 gap-1.5">
                    {category.subcategories
                      .slice(0, expandedCategory === index ? undefined : ITEMS_TO_SHOW)
                      .map((subcategory, subIndex) => (
                        <a
                          key={subIndex}
                          href={GenerateLink.ForSearch(subcategory)}
                          className="group flex items-center justify-between px-3 py-2 rounded-lg
                                 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50
                                 transform transition-all duration-200 ease-in-out"
                        >
                          <span className="transition-transform group-hover:translate-x-1">{subcategory}</span>
                          <svg
                            className="w-4 h-4 opacity-0 group-hover:opacity-100 transform transition-all duration-200 ease-in-out"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </a>
                      ))}
                    {category.subcategories.length > ITEMS_TO_SHOW && (
                      <button
                        onClick={() => handleShowMore(index)}
                        className="w-full text-center py-1 text-sm text-gray-400 hover:text-gray-600
                                 transition-colors duration-200"
                      >
                        {expandedCategory === index ? (
                          <span>Show less</span>
                        ) : (
                          <span>+{category.subcategories.length - ITEMS_TO_SHOW} more</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showModal && selectedCategory !== null && (
        <Modal
          category={categories[selectedCategory]}
          onClose={() => {
            setShowModal(false);
            setSelectedCategory(null);
          }}
        />
      )}

      <div className="w-full bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-8">Shop by Fitment</h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {manufacturers.map((manufacturer, index) => (
              <a key={index} href={manufacturer.link} className="block group">
                <div
                  className="bg-white rounded-xl p-6 h-24 flex items-center justify-center transform transition-all duration-300 ease-in-out
                             shadow hover:shadow-xl hover:-translate-y-1 hover:bg-yellow-50
                             border-2 border-transparent hover:border-yellow-400"
                >
                  <span
                    className="text-gray-800 font-medium text-center group-hover:text-yellow-600 transition-colors
                                 text-lg group-hover:font-semibold"
                  >
                    {manufacturer.title}
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategorySection;
